import { useState, useEffect } from 'react';
import { Monitor, X } from 'lucide-react';

/**
 * Banner shown on small screens to warn users that the current page is
 * designed for desktop use. Dismissible — the user can dismiss it and
 * proceed, but it returns on each fresh page load.
 *
 * Used on the overlay editor and other design-heavy pages where touch
 * input and small viewports will be frustrating.
 */
export default function DesktopOnlyNotice({
  message = 'This page is designed for desktop. Touch input and small screens may make editing difficult.',
}: {
  message?: string;
}) {
  const [isSmall, setIsSmall] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!isSmall || dismissed) return null;

  return (
    <div className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
      <Monitor className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">Best viewed on a larger screen</p>
        <p className="mt-0.5 text-xs opacity-90">{message}</p>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 rounded p-1 hover:bg-amber-100 dark:hover:bg-amber-900/40"
        title="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
